import React, { useState, useEffect, useRef } from 'react';
import {
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Box,
  Typography,
  Container,
  Grid,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import AlertService from '../../components/Alerts/alerts';
import AddressService from './addressService';

const AddAddressForm = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    location: '',
    block: '',
    unit: '',
    label: '',
    latitude: '',
    longitude: '',
    address_url: '',
    city: '',
    state: '',
    postalCode: '',
    country: '',
  });

  const autocompleteRef = useRef(null);
  const inputRef = useRef(null);
  const [storedUser, setStoredUser] = useState(() => JSON.parse(localStorage.getItem('user')));
  const mapRef = useRef(null);
  // Load Google Maps script and initialize the map and autocomplete
  useEffect(() => {
    const initMapAndAutocomplete = () => {
      if (!inputRef.current || autocompleteRef.current) return;

      // Initialize map
      const map = new window.google.maps.Map(document.createElement('div'), {
        zoom: 8,
        center: { lat: 4.2085764, lng: 101.9830197 },
        mapTypeId: 'roadmap',
      });

      // Initialize autocomplete
      const autocomplete = new window.google.maps.places.Autocomplete(inputRef.current, {
        types: ['establishment', 'geocode'],  // Allow both businesses and addresses
        // componentRestrictions: { country: 'my' },
        fields: ['address_components', 'geometry', 'name', 'formatted_address']
      });
      autocompleteRef.current = autocomplete;
      mapRef.current = map;

      // Bind autocomplete to the map's bounds
      autocomplete.bindTo('bounds', map);

      autocomplete.addListener('place_changed', handlePlaceSelect);
    };

    const loadGoogleMapsScript = () => {
      if (window.google && window.google.maps) {
        initMapAndAutocomplete();
      } else {
        const script = document.createElement('script');
        let key = 'AIzaSyDlKtLygdyr4XpjVE1WcyR7yHuigyq_agw'
        script.src = `https://maps.googleapis.com/maps/api/js?key=${key}&libraries=places`;
        script.async = true;
        script.onload = initMapAndAutocomplete;
        document.head.appendChild(script);
      }
    };

    loadGoogleMapsScript();

    return () => {
      if (autocompleteRef.current) {
        window.google.maps.event.clearInstanceListeners(autocompleteRef.current);
        autocompleteRef.current = null;
      }
    };
  }, []);

  // Handle place selection
  const handlePlaceSelect = () => {
    const place = autocompleteRef.current.getPlace();

    if (!place.geometry || !place.geometry.location) {
      AlertService.swalErrorAlert(t('Please select a valid address from the dropdown.'));
      return;
    }

    const lat = place.geometry.location.lat();
    const lng = place.geometry.location.lng();

    // Center map on the selected place
    if (mapRef.current) {
      mapRef.current.setCenter(place.geometry.location);
      mapRef.current.setZoom(17);
    }
    //iph todo: 47
    //console.log("place",place)

    const addressComponents = place.address_components || [];
    const parsedAddress = parseAddressComponents(addressComponents);
 
    //iph todo: 47
    //console.log("parsedAddress",parsedAddress)
    setFormData((prevFormData) => ({
      ...prevFormData,
      ...parsedAddress,
      latitude: lat,
      longitude: lng,
      address_url: place.url || `https://www.google.com/maps?q=${lat},${lng}`,
    }));
  };

  // Parse address components for precision
  const parseAddressComponents = (components) => {
    let block = '';
    let unit = '';
    let city = '';
    let state = '';
    let postalCode = '';
    let country = '';
    const place = autocompleteRef.current.getPlace();
    components.forEach((component) => {
      const types = component.types;
      if (types.includes('street_number')) unit = component.long_name;
      else if (types.includes('route')) block = component.long_name;
      else if (types.includes('sublocality_level_1')) block += `, ${component.long_name}`;
      else if (types.includes('locality')) city = component.long_name;
      else if (types.includes('administrative_area_level_1')) state = component.long_name;
      else if (types.includes('postal_code')) postalCode = component.long_name;
      else if (types.includes('country')) country = component.long_name;
    });

    const location = `${place.name}, ${block} ${unit}, ${city}, ${state} ${postalCode}, ${country}`.trim();
    return { block, unit, city, state, postalCode, country, location };
  };

  // Handle form field changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevFormData) => ({ ...prevFormData, [name]: value }));
  };

  // Submit form data
  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const data = {
        user_id: storedUser?._id,
        ...formData,
      };

      const response = await AddressService.addAddress(data);

      if (response.data.status) {
        AlertService.swalSuccessAlert(t('Address added successfully.'));
        navigate('/doctor-profile');
      } else {
        AlertService.swalErrorAlert(response?.data?.message?.[0]?.message || t('Error adding address.'));
      }
    } catch (error) {
      AlertService.swalErrorAlert(t('Error adding address. Please try again.'));
    }
  };

  return (
    <Container maxWidth="sm">
      <Box sx={{ mt: 4 }}>
        <Typography variant="h4" gutterBottom>
          {t('Add New Address')}
        </Typography>
        <Typography variant="body2" sx={{ mt: 2, mb: 2 }} color="red">
          {t('Please Note: Address must match APC & Borang B/F.')}
        </Typography>
        <form onSubmit={handleSubmit}>
          <TextField
            inputRef={inputRef}
            margin="normal"
            name="location"
            label={t('Search Google Address')}
            fullWidth
            value={formData.location}
            onChange={handleChange}
          />
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                margin="normal"
                name="block"
                label={t('Block or Street name')}
                fullWidth
                value={formData.block}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                margin="normal"
                name="unit"
                label={t('Unit/House no.')}
                fullWidth
                value={formData.unit}
                onChange={handleChange}
              />
            </Grid>
          </Grid>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                margin="normal"
                name="city"
                label={t('City')}
                fullWidth
                value={formData.city}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                margin="normal"
                name="state"
                label={t('State/Province')}
                fullWidth
                value={formData.state}
                onChange={handleChange}
              />
            </Grid>
          </Grid>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                margin="normal"
                name="postalCode"
                label={t('Postal Code')}
                fullWidth
                value={formData.postalCode}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                margin="normal"
                name="country"
                label={t('Country')}
                fullWidth
                value={formData.country}
                onChange={handleChange}
              />
            </Grid>
          </Grid>
          <FormControl fullWidth margin="normal">
            <InputLabel>{t('Label')}</InputLabel>
            <Select
              name="label"
              value={formData.label}
              onChange={handleChange}
            >
              <MenuItem value="Home">{t('Home')}</MenuItem>
              <MenuItem value="Work">{t('Work')}</MenuItem>
              <MenuItem value="Other">{t('Other')}</MenuItem>
            </Select>
          </FormControl>
          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between' }}>
            <Button variant="outlined" onClick={() => navigate(-1)}>
              {t('Cancel')}
            </Button>
            <Button type="submit" variant="contained" color="primary">
              {t('Add')}
            </Button>
          </Box>
        </form>
      </Box>
    </Container>
  );
};

export default AddAddressForm;
